import { useId } from 'react'
import { Mail, Plus, Trash2 } from 'lucide-react'
import type { ExactVelden, FactuurRegel, VerkoopfactuurGegevens } from '../../../gedeeld/types'
import { Knop } from '../ui'
import { DatumVeld, euro, GetalVeld, KeuzeVeld, naamVan, TekstVeld, Totalen, useSleutels, type NaamSoort } from './ExactDelen'

/* ===========================================================================
 *  Een verkoopfactuur op de Exact-kaart
 *
 *  Een echte factuur (salesinvoice), met artikelen. Versturen naar de klant
 *  is een recht apart (exact.facturen_versturen) en staat standaard UIT: een
 *  verkeerde factuur in Exact kun je nog weghalen, een verkeerde factuur in
 *  de mailbox van de klant niet.
 * =========================================================================== */

/* Een nieuwe regel: aantal 1, prijs nog niet bekend (leeg, niet 0). */
const LEGE_REGEL: FactuurRegel = { artikel: '', aantal: 1, prijs: null }

export function ExactVerkoopfactuur({ v, g, onV, gecontroleerd, oud, meldOngeldig, magVersturen }: {
  v: ExactVelden
  g: VerkoopfactuurGegevens
  onV: (v: ExactVelden) => void
  gecontroleerd: boolean
  oud: boolean
  meldOngeldig: (sleutel: string) => (ja: boolean) => void
  magVersturen: boolean
}) {
  const zet = (n: Partial<VerkoopfactuurGegevens>) => onV({ ...v, gegevens: { ...g, ...n } })
  const zetRegel = (i: number, n: Partial<FactuurRegel>) => zet({ regels: g.regels.map((r, j) => (j === i ? { ...r, ...n } : r)) })
  const { ids, erbij, weg } = useSleutels(g.regels.length)
  const naam = (soort: NaamSoort, code: string | undefined) => naamVan(v.berekend, soort, code)
  const id = useId()

  return (
    <>
      <label className="veld">
        <span className="veld-label">Bv</span>
        <select className="invoer" value={v.divisies[0] ?? ''} onChange={(e) => onV({ ...v, divisies: e.target.value ? [e.target.value] : [] })}
          aria-invalid={!v.divisies.length || undefined}>
          {!v.divisies.length && <option value="">Kies een bv…</option>}
          {v.mogelijk.map((d) => <option key={d.code} value={d.code}>{d.naam}</option>)}
        </select>
      </label>
      <TekstVeld label="Klant (code)" mono waarde={g.klant} onWaarde={(klant) => zet({ klant: klant.trim() })}
        naam={naam('relatie', g.klant)} nietGevonden={gecontroleerd} />
      <KeuzeVeld label="Verkoopdagboek" waarde={g.dagboek} onWaarde={(dagboek) => zet({ dagboek })} keuzes={v.keuzes?.dagboeken}
        leeg="Het standaard verkoopdagboek" />
      <DatumVeld label="Factuurdatum" waarde={g.datum} onWaarde={(datum) => zet({ datum })} />
      <TekstVeld label="Omschrijving" breed waarde={g.omschrijving} onWaarde={(omschrijving) => zet({ omschrijving })} />
      <TekstVeld label="Kenmerk van de klant" mono waarde={g.referentie} onWaarde={(referentie) => zet({ referentie })} placeholder="bijv. hun inkoopordernummer" />
      <TekstVeld label="Valuta" mono waarde={g.valuta} onWaarde={(valuta) => zet({ valuta: valuta.toUpperCase() })} placeholder="EUR" />

      <div className="veld breed">
        <span className="veld-label">Regels ({g.regels.length})</span>
        <ol className="ex-regels">
          {g.regels.map((r, i) => {
            const regelTotaal = r.aantal !== null && r.prijs !== null ? Math.round(r.aantal * r.prijs * 100) / 100 : null
            return (
              <li key={ids[i]} className="ex-regel">
                <div className="ex-regel-kop">
                  <span className="ex-regel-nr">Regel {i + 1}</span>
                  {regelTotaal !== null && <span className="ex-regel-som mono">{euro(regelTotaal, g.valuta)} excl.</span>}
                  <Knop klein soort="stil" icoon={<Trash2 size={13} />} aria-label={`Regel ${i + 1} weghalen`} title="Regel weghalen"
                    disabled={g.regels.length <= 1} onClick={() => { weg(i); zet({ regels: g.regels.filter((_, j) => j !== i) }) }} />
                </div>
                <div className="ex-regel-velden">
                  <KeuzeVeld label="Artikel" breed waarde={r.artikel} onWaarde={(artikel) => zetRegel(i, { artikel })} keuzes={v.keuzes?.artikelen}
                    placeholder="artikelcode" onder={!v.keuzes?.artikelen?.length && naam('artikel', r.artikel) ? <span className="ex-naam">{naam('artikel', r.artikel)}</span> : null} />
                  <TekstVeld label="Omschrijving" breed waarde={r.omschrijving} onWaarde={(omschrijving) => zetRegel(i, { omschrijving })}
                    placeholder="optioneel" />
                  <GetalVeld label="Aantal" waarde={r.aantal} decimalen={4} leegMag placeholder="nog niet bekend"
                    onWaarde={(aantal) => zetRegel(i, { aantal })} onOngeldig={meldOngeldig(`a${ids[i]}`)} />
                  <GetalVeld label="Prijs per stuk excl. btw" waarde={r.prijs} decimalen={4} leegMag placeholder="nog niet bekend"
                    onWaarde={(prijs) => zetRegel(i, { prijs })} onOngeldig={meldOngeldig(`p${ids[i]}`)} />
                  <KeuzeVeld label="Btw" waarde={r.btw} onWaarde={(btw) => zetRegel(i, { btw })} keuzes={v.keuzes?.btw} leeg="Geen btw" />
                  <KeuzeVeld label="Kostenplaats" waarde={r.kostenplaats} onWaarde={(kostenplaats) => zetRegel(i, { kostenplaats })}
                    keuzes={v.keuzes?.kostenplaatsen} leeg="Geen" />
                </div>
              </li>
            )
          })}
        </ol>
        <div>
          <Knop klein icoon={<Plus size={13} />} onClick={() => { erbij(); zet({ regels: [...g.regels, { ...LEGE_REGEL }] }) }}>Regel toevoegen</Knop>
        </div>
      </div>

      <Totalen totalen={v.berekend?.totalen} valuta={g.valuta} oud={oud} />

      <div className={`ex-versturen breed ${g.versturen ? 'aan' : ''} ${!magVersturen ? 'uit' : ''}`}>
        <label className="vinkje">
          {/* Zonder recht kun je hem niet AANzetten; wel uit, als de AI hem
              toch aanvinkte -- anders zit je vast aan een kaart die niet kan. */}
          <input type="checkbox" checked={g.versturen} disabled={!magVersturen && !g.versturen}
            onChange={(e) => zet({ versturen: e.target.checked })} aria-describedby={`${id}-mail`} />
          <span><Mail size={13} aria-hidden /> Ook naar de klant mailen</span>
        </label>
        <p id={`${id}-mail`}>
          {magVersturen
            ? <>Let op: Exact mailt de factuur dan <strong>meteen na het aanmaken</strong> naar de klant. Dat is niet terug te halen. Laat het uit als je de factuur eerst zelf wilt bekijken.</>
            : g.versturen
              ? <>Mailen naar de klant mag jij niet (het recht “facturen versturen” staat uit). Zet het vinkje uit; de factuur wordt dan alleen aangemaakt.</>
              : <>Mailen naar de klant staat voor jou uit (het recht “facturen versturen”). De factuur wordt alleen aangemaakt; versturen kan daarna in Exact.</>}
        </p>
      </div>
    </>
  )
}
